import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { TapCorrectScene } from '@/types/gameSpec';
import { shuffle, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

export const TapCorrectSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<TapCorrectScene>) => {
  const order = useMemo(
    () => shuffle(scene.items.map((_, i) => i)),
    [scene.id],
  );
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [revealed, setRevealed] = useState(false);

  const togglePick = (i: number) => {
    if (revealed) return;
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const submit = () => {
    if (revealed) return;
    setRevealed(true);
    const allCorrect = scene.items.every(
      (it, i) => it.correct === picked.has(i),
    );
    setTimeout(
      () => onAnswer(allCorrect, { picked: Array.from(picked) }),
      900,
    );
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.tap_correct}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <div className="flex flex-wrap gap-2">
          {order.map((i) => {
            const it = scene.items[i];
            const isPicked = picked.has(i);
            const showRight = revealed && it.correct;
            const showWrong = revealed && isPicked && !it.correct;
            return (
              <Button
                key={i}
                variant={isPicked ? 'default' : 'outline'}
                onClick={() => togglePick(i)}
                disabled={revealed}
                className={cn(
                  showRight && 'border-green-500 bg-green-100 text-green-900 hover:bg-green-100',
                  showWrong && 'border-destructive bg-red-100 text-red-900 hover:bg-red-100',
                )}
              >
                {it.label}
              </Button>
            );
          })}
        </div>

        {!revealed && (
          <Button onClick={submit} disabled={picked.size === 0}>
            Submit
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
